import { useEffect, useState } from 'react'
import { Square, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTimer, useStopTimer } from '@/hooks/use-timesheet'

/**
 * The running timer in the app header.
 *
 * S5 puts it on every page, and the plan is blunt about why: "a timer you have
 * to navigate to is a timer that stays off". It renders nothing at all when
 * nothing is running, so it costs no space on the pages that do not need it.
 *
 * The elapsed number ticks locally between polls, but the SERVER is the source
 * of truth for what is running and since when — the timer survives a reload and
 * a laptop-to-phone switch precisely because the client does not own it.
 */
function fmtElapsed(startedAt: number, nowSec: number): string {
  const s = Math.max(0, nowSec - startedAt)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

export function HeaderTimer() {
  const { data } = useTimer()
  const stop = useStopTimer()
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000))

  const timer = data?.timer ?? null

  useEffect(() => {
    if (!timer) return
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000)
    return () => clearInterval(id)
  }, [timer])

  if (!timer) return null

  const label = timer.task_title
    ?? (timer.project_identifier ? `${timer.project_identifier}` : timer.project_name)
    ?? 'Running'

  const onStop = () => {
    stop.mutate({}, {
      onSuccess: (r) => {
        if (r.entry) toast.success(`Logged ${Math.round(r.entry.minutes / 60 * 10) / 10}h.`)
      },
      onError: () => {
        // The over-ceiling case is a refusal on purpose: the timer keeps running
        // and the person is asked how long to log, rather than the night being
        // banked silently. Sending them to the timesheet is where they answer.
        toast.error('This timer has run a long time. Open the timesheet to confirm how long to log.')
      },
    })
  }

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11.5px]',
        timer.over_ceiling ? 'border-warn/50 bg-warn/5 text-warn' : 'border-border text-muted-foreground',
      )}
      aria-live="polite"
    >
      {timer.over_ceiling
        ? <AlertTriangle className="h-3 w-3" aria-hidden="true" />
        : <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-sky" aria-hidden="true" />}
      <span className="font-mono tabular-nums" aria-label="Time running">{fmtElapsed(timer.started_at, now)}</span>
      <span className="max-w-[140px] truncate">{label}</span>
      <Button
        size="sm"
        variant="ghost"
        className="h-5 gap-1 px-1.5"
        disabled={stop.isPending}
        onClick={onStop}
        aria-label="Stop the timer"
      >
        <Square className="h-3 w-3" aria-hidden="true" />
      </Button>
    </div>
  )
}
